import { isModuleNotFoundError } from "@tanstack/router-core";
import { Button } from "@/components/ui/button/button";
import { reloadForStaleChunk } from "@/features/shared/app-version";
import { clearChannelDrafts } from "@/features/shared/messaging";

export type ErrorFallbackProps = {
  /** The caught error. Absent only for callers that render this directly. */
  error?: unknown;
  fullScreen?: boolean;
};

export function ErrorFallback({
  error,
  fullScreen = false,
}: ErrorFallbackProps) {
  // A chunk the newer deployment replaced is not a broken session — every route
  // and dialog is its own content-hashed file, so any deploy can strip one out
  // from under a tab. Reload into the same URL instead of accusing the user.
  //
  // Called during render on purpose: an effect would paint the crash screen for
  // a frame first. It is latched, so a double render still reloads once, and it
  // returns false once recovery is spent — then the real error shows and a
  // genuinely broken chunk cannot loop the page.
  if (
    isModuleNotFoundError(error) &&
    reloadForStaleChunk(window.location.href)
  ) {
    return (
      <div
        className={`flex items-center justify-center ${fullScreen ? "h-screen w-screen" : "h-full w-full"}`}
      >
        <p className="text-sm text-text-dark-medium">
          Updating to the latest version…
        </p>
      </div>
    );
  }

  // A stored draft the composer cannot restore survives a plain reload, so the crash
  // screen drops them before reloading.
  const handleReload = () => {
    clearChannelDrafts();
    window.location.reload();
  };
  return (
    <div
      className={`flex items-center justify-center ${fullScreen ? "h-screen w-screen" : "h-full w-full"}`}
    >
      <div className="flex flex-col items-center gap-4 p-6">
        <div className="text-text-dark-high text-center">
          <p className="text-lg font-medium mb-2">Something went wrong</p>
          <p className="text-sm text-text-dark-medium">
            An {fullScreen ? "unexpected " : ""}error occurred
            {fullScreen ? "" : " while loading the page"}. Please try reloading
            {fullScreen ? " the page" : ""}.
          </p>
        </div>
        <Button
          type="button"
          kind="outline"
          variant="neutral"
          onClick={handleReload}
        >
          Reload Page
        </Button>
      </div>
    </div>
  );
}
